/**
 * Sticky product cards on landing pages (Jacob, 2026-10-06, gift-shop-her):
 * while the visitor is looking at a product's feature section and the photo
 * row under it, a small card for that product sits in the bottom-right
 * corner. Scrolling into the next product swaps it, and across the boundary
 * both show for a moment so the previous one is still one click away.
 *
 * Each jr-lp-feature with "Sticky product card" on renders its card into a
 * <template data-jr-zone-card="SECTION_ID">. A zone runs from that section's
 * wrapper down through any photo-row sections directly after it
 * (jr-home-editorial, .jr-edit, or anything marked .jr-zone-follow such as
 * jr-lp-story-cols). A card shows while its zone overlaps the
 * middle band of the viewport (30%-70% of its height), so two neighbouring
 * zones overlap that band for a stretch of scrolling and both cards show.
 * Its entry and exit follow the scroll position (see update below).
 *
 * The page scrolls inside .page-wrapper, not the window, so scroll is heard
 * in the capture phase on document, whichever element scrolls.
 */
(() => {
  if (window.__jrZoneCards) return;
  window.__jrZoneCards = true;

  const init = () => {
    const templates = [...document.querySelectorAll('template[data-jr-zone-card]')];
    if (!templates.length) return;

    const dock = document.createElement('div');
    dock.className = 'jr-zcards';
    document.body.appendChild(dock);

    const zones = templates.map((t) => {
      const wrapper = document.getElementById('shopify-section-' + t.dataset.jrZoneCard);
      if (!wrapper) return null;
      const parts = [wrapper];
      let next = wrapper.nextElementSibling;
      while (next && next.querySelector(':scope > .jr-edit, :scope > .jr-zone-follow') && !next.querySelector('template[data-jr-zone-card]')) {
        parts.push(next);
        next = next.nextElementSibling;
      }
      const card = t.content.firstElementChild.cloneNode(true);
      dock.appendChild(card);
      return { parts, card };
    }).filter(Boolean);

    /* Scroll-linked, not timed (Jacob, 2026-10-06: "feel more like you are
       scrolling it away -- if you reverse it comes back"). Each card has a
       progress 0..1 computed from where its zone sits against the middle
       band, over a ramp of RAMP px of scrolling:
         enter: the zone's top rising past the band's bottom edge
         exit:  the zone's bottom rising past the band's top edge
       Exiting, the card slides up and fades with the page; entering, it
       grows in from below (max-height + opacity), so the card above glides
       up instead of jumping. Scrolling back simply reverses the numbers. */
    const RAMP = 140;
    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    const update = () => {
      const vh = window.innerHeight;
      const bandTop = vh * 0.3;
      const bandBottom = vh * 0.7;
      for (const z of zones) {
        const top = z.parts[0].getBoundingClientRect().top;
        const bottom = z.parts[z.parts.length - 1].getBoundingClientRect().bottom;
        const enter = clamp01((bandBottom - top) / RAMP);
        const exit = clamp01((bottom - bandTop) / RAMP);
        const p = Math.min(enter, exit);
        const on = p > 0;
        z.card.classList.toggle('is-on', on);
        if (!on) continue;
        if (exit < 1) {
          z.card.style.opacity = exit;
          z.card.style.transform = 'translateY(' + (-(1 - exit) * 28).toFixed(1) + 'px)';
          z.card.style.maxHeight = '';
        } else {
          z.card.style.opacity = enter;
          z.card.style.transform = 'translateY(' + ((1 - enter) * 12).toFixed(1) + 'px)';
          z.card.style.maxHeight = (enter * 96).toFixed(1) + 'px';
        }
      }
    };
    /* Straight from the scroll event, no requestAnimationFrame: browsers
       already deliver scroll at most once per frame, the work is four
       getBoundingClientRect calls, and a frame requested while the tab is
       in the background could hold the next update back. */
    document.addEventListener('scroll', update, { capture: true, passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
